import React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import DownloadIcon from "@mui/icons-material/Download";
import { useApiClient } from "@/hooks/useApiClient";
import { getAttachedFileNameFromHeader } from "@/utils/utils";
import fileDownload from "js-file-download";
import { toast } from "react-toastify";
import { PropertyDetailOptionModal } from "@/components/PropertyDetailOptionModal";
import { HttpStatusCode, isAxiosError } from "axios";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import * as Sentry from "@sentry/react";

interface OutputMenuProps {
  disabled: boolean;
  requestId: number;
  isReadOnly?: boolean;
  isPropertyDetailOptionCredit?: boolean;
  onDownloadComplete?: () => void;
}

export default function OutputMenu({
  disabled,
  requestId,
  isReadOnly = false,
  isPropertyDetailOptionCredit = false,
  onDownloadComplete,
}: OutputMenuProps): React.ReactElement {
  const { apiClient } = useApiClient();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openPropertyDetailOptionModal, setOpenPropertyDetailOptionModal] =
    React.useState(false);
  const open = Boolean(anchorEl);
  const isReadOnlyPropertyDetailBlocked =
    isReadOnly && !isPropertyDetailOptionCredit;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const onDownloadFile = async (
    fallBackName: string,
    includePropertyDetail = false
  ): Promise<void> => {
    try {
      const res = await apiClient.get(
        `/all-matters-analyze/download-file/${requestId}`,
        {
          responseType: "blob",
          params: includePropertyDetail
            ? {
                includePropertyDetail: true,
              }
            : undefined,
        }
      );
      const filename = getAttachedFileNameFromHeader(res, fallBackName);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      fileDownload(res.data, filename);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === HttpStatusCode.Forbidden) {
        toast.error(READ_ONLY_TOOLTIP_MESSAGE);
        throw e;
      }
      Sentry.captureException(e);
      toast.error("ファイルのダウンロードに失敗しました");
      throw e;
    }
  };

  return (
    <div>
      <Button
        disabled={disabled}
        variant="outlined"
        color="primary"
        id="output-button"
        aria-controls={open ? "output-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        エクセル出力
        <DownloadIcon />
      </Button>
      <Menu
        id="output-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "output-button",
        }}
      >
        <MenuItem
          onClick={() => {
            void onDownloadFile(`全部事項解析結果_${requestId}.xlsx`);
            handleClose();
          }}
        >
          エクセルダウンロード
        </MenuItem>
        <ReadOnlyTooltip isReadOnly={isReadOnlyPropertyDetailBlocked}>
          <MenuItem
            disabled={isReadOnlyPropertyDetailBlocked}
            onClick={() => {
              if (isPropertyDetailOptionCredit) {
                // 過去に一度実行済みの場合は直接ダウンロード
                void onDownloadFile(
                  `【物件名称・物件住所付き】全部事項解析結果_${requestId}.xlsx`,
                  true
                )
                  .then(() => {
                    onDownloadComplete?.();
                  })
                  .catch(() => undefined);
                handleClose();
              } else {
                // 初回の場合はモーダルを表示
                setOpenPropertyDetailOptionModal(true);
                handleClose();
              }
            }}
          >
            エクセルダウンロード（物件名称・物件住所付き）
          </MenuItem>
        </ReadOnlyTooltip>
      </Menu>
      <PropertyDetailOptionModal
        open={openPropertyDetailOptionModal}
        onClose={() => {
          setOpenPropertyDetailOptionModal(false);
        }}
        onConfirm={async () => {
          await onDownloadFile(
            `【物件名称・物件住所付き】全部事項解析結果_${requestId}.xlsx`,
            true
          );
          onDownloadComplete?.();
        }}
        target="all_matters"
      />
    </div>
  );
}
